
const normalizeFileName = (name: string): string => {
  if (!name || typeof name !== 'string') {
    console.warn("syncImages: Invalid filename", name);
    return '';
  }
  const parts = name.split('/');
  const fileName = parts[parts.length - 1];
  // Remove extensions
  return fileName.replace(/(-1080p|-720p|-1080p-1080p|-4k)?(\.[^/.]+)?$/, '');
};
export const syncVideos = (existingVideos: any[], uploaded: any[], mediaFiles: any[]) => {


  // Filter mediaFiles to include only videos
  const isVideo = (item: any) => {
    const type = item.file?.type || item.type || '';
    const url = item.url || item.file?.url || item.file?.name || '';
    const videoExtensions = [
      '.mp4', '.mov', '.webm', '.avi', '.mkv', '.wmv', '.flv',
      '.mpeg', '.mpg', '.m4v', '.3gp', '.3g2', '.ogv', '.vob',
      '.ts', '.m2ts', '.asf', '.divx', '.xvid', '.rm', '.rmvb'
    ];
    const videoMimeTypes = [
      'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo',
      'video/x-matroska', 'video/x-ms-wmv', 'video/x-flv', 'video/mpeg',
      'video/3gpp', 'video/3gpp2', 'video/ogg', 'video/mp2t', 'video/x-ms-asf'
    ];
    const isValidVideo = (
      type.includes('video') ||
      videoMimeTypes.includes(type.toLowerCase()) ||
      videoExtensions.some(ext => url.toLowerCase().endsWith(ext))
    );
    return isValidVideo;
  };

  const videoMediaFiles = mediaFiles.filter((item: any, index: number) => {
    const isValidVideo = isVideo(item);
    return isValidVideo;
  });

  // Get normalized filenames from videoMediaFiles (UI state) with isFeatured
  const incomingEntries = videoMediaFiles
    .map((item: any) => {
      const rawName = item.url || item.file?.name || item.file?.url || '';
      const normalized = normalizeFileName(rawName);
      return { normalized, isFeatured: !!item?.isFeatured };
    })
    .filter(entry => !!entry.normalized);

  const incomingFileNames = incomingEntries.map(e => e.normalized);
  const incomingMap = new Map<string, boolean>();
  for (const { normalized, isFeatured } of incomingEntries) {
    incomingMap.set(normalized, isFeatured);
  }

  // Filter existing videos to keep only those still in videoMediaFiles, updating isFeatured from UI
  const filteredExisting = Array.isArray(existingVideos)
    ? existingVideos
      .map((video: any) => {
        const normalizedVideoName = normalizeFileName(video.fileName);
        if (!normalizedVideoName || !incomingMap.has(normalizedVideoName)) return null;
        return {
          ...video,
          isFeatured: incomingMap.get(normalizedVideoName) ?? false,
        };
      })
      .filter(Boolean)
    : [];

  // Log removed videos for future use
  const removedVideos = Array.isArray(existingVideos)
    ? existingVideos.filter((video: any) => {
      const normalizedVideoName = normalizeFileName(video.fileName);
      const isRemoved = !incomingFileNames.includes(normalizedVideoName);
      return isRemoved;
    })
    : [];

  // Merge uploaded videos or append missing videoMediaFiles
  let finalVideos: any[] = [];
  if (uploaded.length > 0) {
    finalVideos = [
      ...filteredExisting,
      ...uploaded.map((vid: any, index: number) => {
        const normalizedUploadedName = normalizeFileName(vid.fileName);
        const matchingMedia = videoMediaFiles.find((m: any) =>
          normalizeFileName(m.url || m.file?.name || m.file?.url) === normalizedUploadedName
        );
        return {
          ...vid,
          isFeatured: matchingMedia?.isFeatured || false,
        };
      }),
    ];
  } else {
    finalVideos = [
      ...filteredExisting,
      ...videoMediaFiles
        .filter((m: any) => !filteredExisting.some((e: any) =>
          normalizeFileName(e.fileName) === normalizeFileName(m.url || m.file?.name || m.file?.url)
        ))
        .map((vid: any, index: number) => {
          const rawName = vid.url || vid.file?.name || vid.file?.url;
          const normalized = normalizeFileName(rawName);
          return {
            fileName: rawName, // Preserve full URL/path
            status: 'ready',
            thumb: vid.preview || '',
            versions: [
              { video: rawName, resolution: '1080p' },
            ],
            isFeatured: vid.isFeatured || false,
          };
        }),
    ];
  }
  return finalVideos;
};



export const syncImages = (
  existingImages: any[] = [],
  uploaded: any[] = [],
  mediaFiles: any[] = []
) => {
  // 1) Only keep image-like entries from mediaFiles
  const isImage = (item: any) => {
    const type = item?.file?.type || item?.type || '';
    const url = item?.url || item?.file?.url || item?.file?.name || '';
    const imageExtensions = ['.webp', '.jpg', '.jpeg', '.png', '.heic', '.heif'];
    const imageMimeTypes = ['image/webp', 'image/jpeg', 'image/png', 'image/heic', 'image/heif'];
    return (
      typeof type === 'string' && type.includes('image') ||
      imageMimeTypes.includes(type.toLowerCase()) ||
      (typeof url === 'string' && imageExtensions.some(ext => url.toLowerCase().endsWith(ext)))
    );
  };

  const imageMediaFiles = (Array.isArray(mediaFiles) ? mediaFiles : []).filter(isImage);

  // 2) Build a quick lookup of incoming (UI) names -> isFeatured
  const incomingFileNames = imageMediaFiles
    .map((item: any) => {
      const rawName = item?.url || item?.file?.name || item?.file?.url || '';
      const normalized = normalizeFileName(rawName);
      return { normalized, isFeatured: !!item?.isFeatured };
    })
    .filter(entry => !!entry.normalized);

  const incomingMap = new Map<string, boolean>();
  for (const { normalized, isFeatured } of incomingFileNames) {
    // last write wins (OK for UI state)
    incomingMap.set(normalized, isFeatured);
  }

  // 3) Keep existing images that are still present in UI; if present, UI decides isFeatured
  const filteredExisting = (Array.isArray(existingImages) ? existingImages : [])
    .map((image: any) => {
      const normalizedImageName = normalizeFileName(image?.fileName);
      if (!normalizedImageName) return null;
      if (!incomingMap.has(normalizedImageName)) return null;
      return {
        ...image,
        isFeatured: incomingMap.get(normalizedImageName) ?? false,
      };
    })
    .filter(Boolean);

  // 4) Identify removed images (kept in case you need them later)
  const removedImages = (Array.isArray(existingImages) ? existingImages : []).filter((image: any) => {
    const normalizedImageName = normalizeFileName(image?.fileName);
    return normalizedImageName && !incomingMap.has(normalizedImageName);
  });
  // (not used further, but available if you need it)

  // 5) Merge uploaded
  let finalImages: any[] = [];

  if (Array.isArray(uploaded) && uploaded.length > 0) {
    finalImages = [
      ...filteredExisting,
      ...uploaded.map((img: any) => {
        const normalizedUploadedName = normalizeFileName(img?.fileName);
        const uiFlag = normalizedUploadedName ? incomingMap.get(normalizedUploadedName) : undefined;

        return {
          ...img,
          // If UI has a flag, use that; otherwise keep uploaded’s own flag (don’t force false)
          isFeatured: (uiFlag !== undefined ? uiFlag : !!img?.isFeatured),
        };
      }),
    ];
  } else {
    // No uploaded; add items from mediaFiles that aren't already in filteredExisting
    const existingSet = new Set(
      filteredExisting
        .map(e => normalizeFileName(e?.fileName))
        .filter(Boolean)
    );

    const newlyBuilt = imageMediaFiles
      .filter((m: any) => {
        const candidate = normalizeFileName(m?.url || m?.file?.name || m?.file?.url);
        return candidate && !existingSet.has(candidate);
      })
      .map((img: any) => {
        const rawName = img?.url || img?.file?.name || img?.file?.url || '';
        return {
          fileName: rawName, // preserve full value
          status: 'ready',
          thumb: img?.preview || '',
          versions: [{ image: rawName, resolution: 'original' }],
          isFeatured: !!img?.isFeatured,
        };
      });

    finalImages = [...filteredExisting, ...newlyBuilt];
  }

  // 6) Enforce a single featured image (keep the first one in final order)
  const firstFeaturedIndex = finalImages.findIndex(x => !!x?.isFeatured);
  if (firstFeaturedIndex !== -1) {
    finalImages = finalImages.map((img, idx) => ({
      ...img,
      isFeatured: idx === firstFeaturedIndex,
    }));
  }

  return finalImages;
};



export const syncAudios = (existingAudios: any[], uploaded: any[], mediaFiles: any[]) => {



  // Filter mediaFiles to include only audios
  const isAudio = (item: any) => {
    const type = item.file?.type || item.type || '';
    const url = item.url || item.file?.url || item.file?.name || '';
    const audioExtensions = ['.mp3', '.aac', '.wav', '.m4a'];
    const audioMimeTypes = ['audio/mpeg', 'audio/aac', 'audio/wav', 'audio/x-m4a', 'audio/m4a'];
    const isValidAudio = (
      type.includes('audio') ||
      audioMimeTypes.includes(type.toLowerCase()) ||
      audioExtensions.some(ext => url.toLowerCase().endsWith(ext))
    );
    return isValidAudio;
  };

  const audioMediaFiles = mediaFiles.filter((item: any, index: number) => {
    const isValidAudio = isAudio(item);
    return isValidAudio;
  });

  // Get normalized filenames from audioMediaFiles (UI state)
  const incomingFileNames = audioMediaFiles
    .map((item: any, index: number) => {
      const rawName = item.url || item.file?.name || item.file?.url || '';
      const normalized = normalizeFileName(rawName);
      return normalized;
    })
    .filter(Boolean);

  // Filter existing audios to keep only those in audioMediaFiles
  const filteredExisting = Array.isArray(existingAudios)
    ? existingAudios.filter((audio: any) => {
      const normalizedAudioName = normalizeFileName(audio.fileName);
      const isIncluded = incomingFileNames.includes(normalizedAudioName);
      return isIncluded;
    })
    : [];

  //Removed audio files for future use
  const removedAudios = Array.isArray(existingAudios)
    ? existingAudios.filter((audio: any) => {
      const normalizedAudioName = normalizeFileName(audio.fileName);
      const isRemoved = !incomingFileNames.includes(normalizedAudioName);
      return isRemoved;
    })
    : [];

  // Merge uploaded audios or append missing audioMediaFiles
  let finalAudios: any[] = [];
  if (uploaded.length > 0) {
    finalAudios = [
      ...filteredExisting,
      ...uploaded.map((aud: any) => ({
        fileName: aud.fileName,
        status: aud.status || 'ready',
      })),
    ];
  } else {
    finalAudios = [
      ...filteredExisting,
      ...audioMediaFiles
        .filter((m: any) => !filteredExisting.some((e: any) =>
          normalizeFileName(e.fileName) === normalizeFileName(m.url || m.file?.name || m.file?.url)
        ))
        .map((aud: any, index: number) => {
          const rawName = aud.url || aud.file?.name || aud.file?.url;
          const normalized = normalizeFileName(rawName);
          return {
            fileName: rawName,
            status: 'ready',
          };
        }),
    ];
  }
  return finalAudios;
};